import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  LocalAlertConfig,
  LocalAlertCreateProps,
} from '../../types/localeAlerts';
import { createLocalAlert } from '../../utils/alerts';

type LocalAlertContextType = {
  alerts: LocalAlertConfig[];
  addAlerts: (alerts: LocalAlertCreateProps | LocalAlertCreateProps[]) => void;
  removeAlert: (id: string) => void;
  clearAlerts: () => void;
};

const LocalAlertContext = createContext<LocalAlertContextType | null>(null);

/**
 * Provider used for adding/displaying alerts within a given organism or page experience.
 */
export const LocalAlertProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [alerts, setAlerts] = useState<LocalAlertConfig[]>([]);

  const addAlerts = useCallback(
    (newAlerts: LocalAlertCreateProps | LocalAlertCreateProps[]) => {
      setAlerts((prevAlerts) => {
        const alertsToAdd = Array.isArray(newAlerts)
          ? newAlerts.map(createLocalAlert)
          : [createLocalAlert(newAlerts)];
        return [...prevAlerts, ...alertsToAdd];
      });
    },
    [],
  );

  const removeAlert = useCallback((id: string) => {
    setAlerts((prevAlerts) => prevAlerts.filter((a) => a.id !== id));
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  // TODO: maybe we have a timer to close out some alerts?
  const contextValue = useMemo(() => {
    return {
      addAlerts,
      removeAlert,
      clearAlerts,
      alerts,
    };
  }, [JSON.stringify(alerts)]);
  return (
    <LocalAlertContext.Provider value={contextValue}>
      {children}
    </LocalAlertContext.Provider>
  );
};

/**
 * Local alerts context.
 */
export const useLocalAlerts = () => {
  const context = useContext(LocalAlertContext);
  if (!context) {
    throw new Error('useAlertContext must be used within AlertProvider');
  }
  return context;
};
